import React, { useState, useEffect, useMemo } from 'react'
import { api } from '../api'
import ExportButton from '../components/ExportButton'
import LastUpdated from '../components/LastUpdated'

const PLATFORM_KEYS = ['iosMobile', 'androidMobile', 'cmc', 'ipad', 'androidTablet', 'windowsApp', 'mac']
const PLATFORM_LABELS = {
  iosMobile: 'iOS Mobile',
  androidMobile: 'Android Mobile',
  cmc: 'CMC',
  ipad: 'iPad',
  androidTablet: 'Android Tablet',
  windowsApp: 'Win App',
  mac: 'Mac',
}

const STATUS_OPTIONS = ['GA', 'External Flight', 'Beta Flight', 'Dev', 'RFC', 'Not started', 'No plan', 'NA']
const FLIGHT_WEEKS = ['', 'W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8']
const PICASSO_STATUSES = ['Done', 'RFC', 'Dev', 'Not started']

const statusEmoji = {
  'GA': '✅', 'External Flight': '✈️', 'Beta Flight': '🛩️', 'Dev': '🔨',
  'RFC': '📋', 'Not started': '⬜', 'No plan': '—', 'NA': '', 'Done': '✅',
}
const statusColors = {
  'GA': { bg: '#E8F5E9', text: '#2E7D32' },
  'Done': { bg: '#E8F5E9', text: '#2E7D32' },
  'External Flight': { bg: '#E0F2F1', text: '#00695C' },
  'Beta Flight': { bg: '#F3E5F5', text: '#7B1FA2' },
  'Dev': { bg: '#E3F2FD', text: '#1565C0' },
  'RFC': { bg: '#FFF8E1', text: '#F57F17' },
  'Not started': { bg: '#F5F5F5', text: '#616161' },
  'No plan': { bg: 'transparent', text: '#9E9E9E' },
  'NA': { bg: '#FAFAFA', text: '#BDBDBD' },
}

function formatEta(eta) {
  if (!eta) return '—'
  // If it's a date string like "2026-03-18", format nicely
  const d = new Date(eta)
  if (!isNaN(d.getTime()) && eta.includes('-')) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  return eta
}

function StatusCell({ status, eta }) {
  if (status === 'NA') return <span style={{ color: '#ccc' }}>N/A</span>
  if (status === 'No plan') return <span style={{ color: '#bbb' }}>—</span>
  const c = statusColors[status] || {}
  return (
    <div className="parity-cell">
      <span className="badge" style={{ background: c.bg, color: c.text }}>{statusEmoji[status]} {status}</span>
      {eta && <span className="parity-eta">{formatEta(eta)}</span>}
    </div>
  )
}

function ParityPct({ value }) {
  if (!value) return <span>—</span>
  const num = parseInt(value)
  const cls = num > 50 ? 'parity-pct-green' : num > 0 ? 'parity-pct-yellow' : 'parity-pct-red'
  return <span className={cls}>{value}</span>
}

export default function FeatureParity() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingCell, setEditingCell] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [sortConfig, setSortConfig] = useState({ key: 'parity', direction: 'desc' })
  const [adding, setAdding] = useState(null) // 'client' or 'picasso'
  const [newFeature, setNewFeature] = useState('')
  const [newMission, setNewMission] = useState('')

  useEffect(() => {
    api.getFeatureParity()
      .then(data => setItems(data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const clientItems = useMemo(() => items.filter(i => i.type === 'client'), [items])
  const picassoItems = useMemo(() => items.filter(i => i.type === 'picasso'), [items])

  const missions = useMemo(() => [...new Set(items.map(i => i.mission).filter(Boolean))].sort(), [items])

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' }
        return { key: null, direction: 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const sortItems = (items) => {
    if (!sortConfig.key) return items
    return [...items].sort((a, b) => {
      const key = sortConfig.key
      let av, bv
      if (key === 'parity') {
        av = parseInt(a.parity) || 0
        bv = parseInt(b.parity) || 0
      } else {
        av = String(a[key] || '').toLowerCase()
        bv = String(b[key] || '').toLowerCase()
      }
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }

  const SortHeader = ({ field, children, className }) => (
    <th className={className} onClick={() => handleSort(field)} style={{ cursor: 'pointer' }}>
      {children}
      {sortConfig.key === field && <span className="sort-arrow">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
    </th>
  )

  const startEdit = (id, field, currentValue) => {
    setEditingCell({ id, field })
    setEditValue(currentValue || '')
  }

  const computeParity = (platforms) => {
    if (!platforms) return '0%'
    const entries = Object.values(platforms).filter(p => p && p.status && p.status !== 'NA')
    if (entries.length === 0) return '0%'
    const ga = entries.filter(p => p.status === 'GA' || p.status === 'Done').length
    return `${Math.round(ga / entries.length * 100)}% (${ga}/${entries.length})`
  }

  const saveEdit = async () => {
    if (!editingCell) return
    setSaving(true)
    try {
      const { id, field } = editingCell
      const item = items.find(i => i.id === id)
      if (!item) return

      let updateBody = {}

      // Handle nested platform fields like "platforms.iosMobile.status"
      if (field.startsWith('platforms.')) {
        const parts = field.split('.')
        const platformKey = parts[1]
        const subField = parts[2]
        const currentPlatforms = { ...item.platforms }
        currentPlatforms[platformKey] = { ...currentPlatforms[platformKey], [subField]: editValue }
        // Auto-recalculate parity% when status changes
        const newParity = computeParity(currentPlatforms)
        updateBody = { platforms: currentPlatforms, parity: newParity }
      } else {
        updateBody = { [field]: editValue }
      }

      await api.updateFeatureParity(id, updateBody)
      setItems(prev => prev.map(i => {
        if (i.id !== id) return i
        if (field.startsWith('platforms.')) {
          const parts = field.split('.')
          const newPlatforms = {
            ...i.platforms,
            [parts[1]]: { ...i.platforms?.[parts[1]], [parts[2]]: editValue }
          }
          return { ...i, platforms: newPlatforms, parity: computeParity(newPlatforms) }
        }
        return { ...i, [field]: editValue }
      }))
    } catch (e) { console.error(e) }
    setEditingCell(null)
    setSaving(false)
  }

  const renderEditableStatus = (item, field, value, options = STATUS_OPTIONS) => {
    const isEditing = editingCell?.id === item.id && editingCell?.field === field
    if (isEditing) {
      return (
        <select autoFocus value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} className="edit-select">
          {options.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )
    }
    return (
      <span className="editable-cell" onClick={() => startEdit(item.id, field, value)}>
        <StatusCell status={value} />
      </span>
    )
  }

  const renderEditableEta = (item, field, value) => {
    const isEditing = editingCell?.id === item.id && editingCell?.field === field
    if (isEditing) {
      return (
        <input autoFocus type="date" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} className="edit-input" style={{ width: '140px' }} />
      )
    }
    return (
      <span className="editable-cell" onClick={() => startEdit(item.id, field, value)}>
        {formatEta(value) || '—'}
      </span>
    )
  }

  const renderEditableMission = (item) => {
    const isEditing = editingCell?.id === item.id && editingCell?.field === 'mission'
    if (isEditing) {
      return (
        <input autoFocus type="text" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={e => e.key === 'Enter' && saveEdit()} className="edit-input" style={{ width: '120px' }} />
      )
    }
    return (
      <span className="editable-cell" onClick={() => startEdit(item.id, 'mission', item.mission)}>
        {item.mission || '—'}
      </span>
    )
  }

  const renderEditableFeature = (item) => {
    const isEditing = editingCell?.id === item.id && editingCell?.field === 'feature'
    if (isEditing) {
      return (
        <input autoFocus type="text" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={e => e.key === 'Enter' && saveEdit()} className="edit-input" style={{ width: '200px' }} />
      )
    }
    return (
      <span className="editable-cell" onClick={() => startEdit(item.id, 'feature', item.feature)}>
        {item.feature || '—'}
      </span>
    )
  }

  const handleAdd = async (type) => {
    if (!newFeature.trim()) return
    setSaving(true)
    try {
      const body = { type, feature: newFeature.trim(), mission: newMission.trim() || '' }
      if (type === 'client') {
        body.platforms = {}
        PLATFORM_KEYS.forEach(k => { body.platforms[k] = { status: 'NA', eta: '' } })
        body.parity = '0%'
      } else {
        body.status = 'Not started'
        body.eta = ''
      }
      const result = await api.createFeatureParity(body)
      setItems(prev => [...prev, result])
      setAdding(null)
      setNewFeature('')
      setNewMission('')
    } catch (e) { console.error(e) }
    setSaving(false)
  }

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.feature}"?`)) return
    try {
      await api.deleteFeatureParity(item.id)
      setItems(prev => prev.filter(i => i.id !== item.id))
    } catch (e) { console.error(e) }
  }

  const renderPlatformCell = (item, platformKey) => {
    const platform = item.platforms?.[platformKey]
    const status = platform?.status || 'NA'
    const eta = platform?.eta || ''
    const statusField = `platforms.${platformKey}.status`
    const etaField = `platforms.${platformKey}.eta`

    const isEditingStatus = editingCell?.id === item.id && editingCell?.field === statusField
    const isEditingEta = editingCell?.id === item.id && editingCell?.field === etaField

    if (isEditingStatus) {
      return (
        <select autoFocus value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} className="edit-select">
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )
    }
    if (isEditingEta) {
      return (
        <input autoFocus type="date" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={saveEdit} className="edit-input" style={{ width: '140px' }} />
      )
    }

    return (
      <div className="parity-cell">
        <span className="editable-cell" onClick={() => startEdit(item.id, statusField, status)}>
          <StatusCell status={status} />
        </span>
        {status !== 'NA' && status !== 'No plan' && (
          <span className="editable-cell parity-eta" onClick={() => startEdit(item.id, etaField, eta)}>
            {formatEta(eta) || '—'}
          </span>
        )}
      </div>
    )
  }

  if (loading) return <div className="loading">Loading feature parity data...</div>

  return (
    <div className="feature-parity-page fade-in">
      <div className="page-header">
        <h2>📊 Feature Parity</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LastUpdated data={items} />
          <ExportButton data={items} filename="feature-parity" tabType="feature-parity" />
        </div>
      </div>

      {/* Client Features */}
      <div className="section-card">
        <h3>🖥️ Client Features ({clientItems.length})</h3>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="data-table parity-table">
            <thead>
              <tr>
                <SortHeader field="mission" className="mission-col">Mission</SortHeader>
                <SortHeader field="feature" className="feature-col">Feature</SortHeader>
                <SortHeader field="parity">Parity%</SortHeader>
                {PLATFORM_KEYS.map(pk => (
                  <th key={pk}>{PLATFORM_LABELS[pk]}</th>
                ))}
                <th style={{ width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {sortItems(clientItems).map(item => (
                <tr key={item.id}>
                  <td className="mission-col">{renderEditableMission(item)}</td>
                  <td className="feature-col">{renderEditableFeature(item)}</td>
                  <td><ParityPct value={item.parity} /></td>
                  {PLATFORM_KEYS.map(pk => (
                    <td key={pk}>{renderPlatformCell(item, pk)}</td>
                  ))}
                  <td><button className="delete-row-btn" onClick={() => handleDelete(item)} title="Delete">🗑️</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {adding === 'client' ? (
          <div className="add-row-form">
            <input type="text" placeholder="Feature name" value={newFeature} onChange={e => setNewFeature(e.target.value)} className="edit-input" />
            <input type="text" placeholder="Mission" value={newMission} onChange={e => setNewMission(e.target.value)} className="edit-input" />
            <button className="save-btn" disabled={saving || !newFeature.trim()} onClick={() => handleAdd('client')}>{saving ? '...' : 'Add'}</button>
            <button className="cancel-btn" onClick={() => { setAdding(null); setNewFeature(''); setNewMission('') }}>Cancel</button>
          </div>
        ) : (
          <button className="add-row-btn" onClick={() => setAdding('client')}>＋ Add Feature</button>
        )}
      </div>

      {/* Picasso Features */}
      <div className="section-card">
        <h3>🎨 Picasso Features ({picassoItems.length})</h3>
        <table className="data-table parity-table">
          <thead>
            <tr>
              <SortHeader field="feature" className="feature-col">Feature</SortHeader>
              <SortHeader field="mission" className="mission-col">Mission</SortHeader>
              <th>Status</th>
              <th>ETA</th>
              <th style={{ width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sortItems(picassoItems).map(item => (
              <tr key={item.id}>
                <td className="feature-col">{renderEditableFeature(item)}</td>
                <td className="mission-col">{renderEditableMission(item)}</td>
                <td>{renderEditableStatus(item, 'status', item.status, PICASSO_STATUSES)}</td>
                <td>{renderEditableEta(item, 'eta', item.eta)}</td>
                <td><button className="delete-row-btn" onClick={() => handleDelete(item)} title="Delete">🗑️</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {adding === 'picasso' ? (
          <div className="add-row-form">
            <input type="text" placeholder="Feature name" value={newFeature} onChange={e => setNewFeature(e.target.value)} className="edit-input" />
            <input type="text" placeholder="Mission" value={newMission} onChange={e => setNewMission(e.target.value)} className="edit-input" />
            <button className="save-btn" disabled={saving || !newFeature.trim()} onClick={() => handleAdd('picasso')}>{saving ? '...' : 'Add'}</button>
            <button className="cancel-btn" onClick={() => { setAdding(null); setNewFeature(''); setNewMission('') }}>Cancel</button>
          </div>
        ) : (
          <button className="add-row-btn" onClick={() => setAdding('picasso')}>＋ Add Feature</button>
        )}
      </div>
    </div>
  )
}
